import { useTranslation } from '@hbs-us/ui';
import { useEffect, useId, useRef } from 'react';

import { AssignmentCardActions } from './AssignmentCardActions.js';
import type { DispatchAssignmentDto } from './contractsMirror.js';
import { formatSpanLabel, hasAssumedEnd, resolveSpan } from './timeGrid.js';

/**
 * The detail panel for one block on the week board.
 *
 * A block on a time grid is sized by its duration, so a half-hour job is about 28 pixels tall and
 * cannot hold the three action buttons the old stacked card carried. Rather than hide the actions
 * on short jobs, everything the card used to show moves here: the block itself carries only what
 * survives being read at a glance, and this panel carries the rest — crew, customer, phone,
 * address, notes, the scheduled time, and the Copy link / Edit / Remove actions.
 *
 * It opens on CLICK, not hover. Hover is what ServiceTitan does, but a hover-only affordance is
 * unreachable from the keyboard, and a panel that appears under the pointer mid-drag would fight
 * the drag the board is built around. The `PointerSensor` already has a 4px activation distance, so
 * a click that does not move is a click and a drag is a drag.
 *
 * Anchored beside its block and flipped away from the edge on the last columns of the week. Escape
 * closes, a pointer-down outside closes, and focus returns to the block that opened it — the same
 * contract as the dialogs in this feature. Token-only styling except the server-owned category
 * colour, and the category is always named in TEXT as well as painted (A11Y-01).
 */
export function AssignmentDetailPopover({
  assignment,
  side,
  onClose,
  onEdit,
  onUnassign,
}: {
  assignment: DispatchAssignmentDto;
  /** Which way to open, so a panel on Saturday does not run off the board. */
  side: 'left' | 'right';
  onClose: () => void;
  onEdit: (assignment: DispatchAssignmentDto) => void;
  onUnassign: (assignment: DispatchAssignmentDto) => void;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    }
    function onPointerDown(event: PointerEvent) {
      const node = panelRef.current;
      if (node && event.target instanceof Node && !node.contains(event.target)) onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    // Capture phase: the block underneath is a drag handle, and a pointer-down that closes the
    // panel must not also be read as the start of a drag on whatever is beneath it.
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown, true);
    };
  }, [onClose]);

  useEffect(() => {
    panelRef.current?.focus();
  }, []);

  const span = resolveSpan(assignment);
  const assumedEnd = hasAssumedEnd(assignment);
  const title = assignment.categoryName ?? assignment.jobName ?? assignment.employeeName;

  return (
    // A non-modal role="dialog" that takes focus on open, so Escape and Tab land inside it. It is
    // dismissed rather than submitted, so deliberately not a focus TRAP like the edit dialog.
    <div
      ref={panelRef}
      role="dialog"
      aria-labelledby={headingId}
      tabIndex={-1}
      className={`absolute top-0 z-30 w-72 rounded-md border border-border bg-card p-3 shadow-lg focus-visible:outline-none ${
        side === 'right' ? 'left-full ml-2' : 'right-full mr-2'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          {assignment.categoryColor ? (
            <span
              aria-hidden="true"
              className="h-3 w-3 shrink-0 rounded-sm"
              style={{ backgroundColor: assignment.categoryColor }}
            />
          ) : null}
          <h3 id={headingId} className="font-medium text-foreground text-sm">
            {title}
          </h3>
        </div>
        {assignment.jobCode ? (
          <span className="shrink-0 text-muted-foreground text-xs">{assignment.jobCode}</span>
        ) : null}
      </div>

      <dl className="mt-2 flex flex-col gap-1 text-xs">
        <Row label={t('dispatch:detail.assignedTo')} value={assignment.employeeName} />
        <Row label={t('dispatch:detail.job')} value={assignment.jobName ?? assignment.clientName} />
        <Row
          label={t('dispatch:detail.customer')}
          value={assignment.customerName ?? assignment.clientName}
        />
        {assignment.customerPhone ? (
          <div className="flex gap-1">
            <dt className="text-muted-foreground">{t('dispatch:detail.phone')}</dt>
            <dd>
              <a
                href={`tel:${assignment.customerPhone}`}
                className="text-primary-ink underline-offset-2 hover:underline"
              >
                {assignment.customerPhone}
              </a>
            </dd>
          </div>
        ) : null}
        <Row label={t('dispatch:detail.address')} value={assignment.customerAddress} />
        <Row
          label={t('dispatch:detail.scheduled')}
          value={
            span
              ? `${assignment.scheduledDate} · ${formatSpanLabel(span.startMin, span.endMin)}`
              : assignment.scheduledDate
          }
        />
        {assumedEnd ? (
          <p className="text-muted-foreground italic">{t('dispatch:detail.assumedEnd')}</p>
        ) : null}
        {assignment.status ? (
          <Row
            label={t('dispatch:detail.status')}
            value={t(`dispatch:status.${assignment.status}`, {
              defaultValue: assignment.status,
            })}
          />
        ) : null}
      </dl>

      {assignment.notes ? (
        <p className="mt-2 whitespace-pre-line border-border border-t pt-2 text-foreground text-xs">
          {assignment.notes}
        </p>
      ) : null}

      <AssignmentCardActions assignment={assignment} onEdit={onEdit} onUnassign={onUnassign} />
    </div>
  );
}

/** One label/value pair, omitted entirely when there is no value rather than shown empty. */
function Row({ label, value }: { label: string; value: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-1">
      <dt className="shrink-0 text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}
